import { useQuery } from '@tanstack/react-query';
import { useCustomInstance } from '@/lib/use-custom-instance';

/**
 * INTERIM data layer for the email-verification state endpoint.
 *
 * Mirrors the api's `EmailVerificationStateDto`. The state is Redis-backed on the
 * server so it survives a page refresh — the verification step reads it to decide
 * whether to send on mount and to render the correct resend cooldown, instead of
 * blindly re-sending the email every remount.
 *
 * Once the api is reachable in codegen, `yarn generate-api` emits
 * `useAuthenticationControllerEmailVerificationState` in `src/lib/endpoints.ts`;
 * swap this for the generated hook then (same `useCustomInstance` mutator).
 */
export interface EmailVerificationState {
  isEmailVerified: boolean;
  emailSent: boolean;
  lastSentAt: string | null;
  cooldownSecondsLeft: number;
  canResend: boolean;
}

export const emailVerificationStateKey = (): [string] => ['/auth/email-verification-state'];

/**
 * Current email-verification send state for the authenticated user.
 *
 * Fetched once on mount (i.e. on a page refresh) and otherwise only when
 * explicitly invalidated — it is NOT polled. Verification itself is detected
 * client-side from Firebase; this endpoint only carries the send/cooldown state
 * so a refresh doesn't re-send and shows the right resend countdown.
 */
export function useEmailVerificationState(enabled = true) {
  const request = useCustomInstance<EmailVerificationState>();
  return useQuery({
    queryKey: emailVerificationStateKey(),
    queryFn: () => request({ url: '/auth/email-verification-state', method: 'GET' }),
    enabled,
    refetchOnWindowFocus: false,
    staleTime: 30_000,
  });
}
